import React, { useState } from "react";
import Modal from "react-modal";
import { IoIosClose } from "react-icons/io";
import { useLoader } from "../../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../../base/context/MessageProvider";
import { orderApiService } from "../../../../../service/seller/orderApiService";
import "./style.css";

const customStyles = {
  overlay: {
    zIndex: 1050,
    background: "rgba(15, 23, 42, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  content: {
    position: "relative",
    inset: "auto",
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: "12px",
    background: "#fff",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)",
    maxWidth: "400px",
    width: "100%",
    maxHeight: "calc(100vh - 48px)",
    overflow: "auto",
  },
};

export default function RaiseTicket({
  modalIsOpen,
  setIsOpen,
  id,
  orderIds = [],
  bulkOrderIds = [],
  processingOrderIds = [],
  handleRefresh,
}) {
  const [data, setData] = useState({
    issue: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const effectiveBulkIds =
    Array.isArray(bulkOrderIds) && bulkOrderIds.length ? bulkOrderIds : orderIds;
  const processingIds = Array.isArray(processingOrderIds) ? processingOrderIds : [];

  const isBulkTicket = (effectiveBulkIds && effectiveBulkIds.length > 0) || processingIds.length > 0;
  const totalBulkCount =
    (effectiveBulkIds && effectiveBulkIds.length ? effectiveBulkIds.length : 0) +
    processingIds.length;

  const message = useMessage();
  const loader = useLoader();

  function closeModal() {
    if (!submitting) setIsOpen(false);
  }

  const handleOnChange = (e) => {
    setData({ ...data, issue: e.target.value });
    setError("");
  };

  const validate = () => {
    if (!data.issue || !String(data.issue).trim()) {
      setError("Issue is required");
      return false;
    }
    return true;
  };

  const handleRaiseTicket = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    loader.showLoading(true);

    try {
      const issuePayload = JSON.stringify({ issue: data.issue.trim() });

      // If bulk modal but no IDs were passed, fallback to single behaviour.
      const bulkIds = effectiveBulkIds && effectiveBulkIds.length ? effectiveBulkIds : [];
      const processingIdsToHandle = processingIds || [];

      const hasBulkIds = bulkIds.length > 0;
      const hasProcessingIds = processingIdsToHandle.length > 0;

      if (!hasBulkIds && !hasProcessingIds) {
        // Handle single ticket raising
        const res = await orderApiService.raiseTicket(id, issuePayload);
        loader.showLoading(false);
        setSubmitting(false);

        if (res.status.code === 200) {
          message.showToast(res.status.message || "Ticket created successfully", ToastTypes.SUCCESS);
          closeModal();
          if (handleRefresh) handleRefresh();
        } else {
          message.showToast(res.error?.message || "Failed to create ticket", ToastTypes.ERROR);
        }
        return;
      }

      let successCount = 0;
      const failureMessages = [];

      if (hasBulkIds) {
        try {
          const res = await orderApiService.raiseBulkTicket({
            orderIds: bulkIds,
            issue: data.issue,
          });

          if (res.status.code === 200) {
            successCount += bulkIds.length;
          } else {
            failureMessages.push(res.error?.message || "Failed to raise tickets for selected orders");
          }
        } catch (err) {
          const errorMessage =
            err?.error?.message || err?.message || "Failed to raise tickets for selected orders";
          failureMessages.push(errorMessage);
        }
      }

      if (hasProcessingIds) {
        for (const orderId of processingIdsToHandle) {
          try {
            const res = await orderApiService.raiseTicket(orderId, issuePayload);

            if (res.status.code === 200) {
              successCount += 1;
            } else {
              failureMessages.push(
                res.error?.message || `Failed to raise ticket for order ${orderId}`
              );
            }
          } catch (err) {
            const errorMessage =
              err?.error?.message || err?.message || `Failed to raise ticket for order ${orderId}`;
            failureMessages.push(errorMessage);
          }
        }
      }

      loader.showLoading(false);
      setSubmitting(false);

      if (successCount > 0) {
        message.showToast(
          `Tickets raised for ${successCount} order(s) successfully`,
          ToastTypes.SUCCESS
        );
        closeModal();
        if (handleRefresh) handleRefresh();
      }

      if (failureMessages.length > 0) {
        message.showToast(failureMessages[0], ToastTypes.ERROR);
      }
    } catch (err) {
      loader.showLoading(false);
      setSubmitting(false);
      message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
    }
  };

  const modalTitle = isBulkTicket
    ? `Raise Tickets for ${totalBulkCount} Orders`
    : "Raise a Ticket";
  const hintText = isBulkTicket
    ? "This will create a ticket for each of the selected orders. The same message will be sent for all."
    : "This ticket will appear in Ticket Management and the admin will see it on this order.";
  const labelText = isBulkTicket ? "Message for selected orders" : "Describe your issue or message";

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Raise ticket for order"
      ariaHideApp={typeof document !== "undefined"}
    >
      <div className="seller-raise-ticket-modal">
        <div className="seller-raise-ticket-modal-header">
          <h2 className="seller-raise-ticket-modal-title">{modalTitle}</h2>
          <button
            type="button"
            className="seller-raise-ticket-modal-close"
            onClick={closeModal}
            aria-label="Close"
          >
            <IoIosClose size={24} />
          </button>
        </div>

        <form onSubmit={handleRaiseTicket}>
          <div className="seller-raise-ticket-modal-body">
            <p className="seller-raise-ticket-modal-hint">{hintText}</p>
            <label htmlFor="seller-raise-ticket-issue" className="seller-raise-ticket-modal-label">
              {labelText}
            </label>
            <textarea
              id="seller-raise-ticket-issue"
              className={`seller-raise-ticket-modal-textarea ${error ? "seller-raise-ticket-modal-textarea-error" : ""}`}
              value={data.issue}
              onChange={handleOnChange}
              placeholder="Describe the issue or message..."
              rows={4}
              disabled={submitting}
            />
            {error ? <p className="seller-raise-ticket-modal-error">{error}</p> : null}
          </div>

          <div className="seller-raise-ticket-modal-footer">
            <button
              type="button"
              className="seller-raise-ticket-modal-btn-cancel"
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="seller-raise-ticket-modal-btn-submit"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
